import { useState, useEffect, useCallback } from 'react';
import { fetchEasykitBrowse, fetchEasykitRecentProjects, deleteEasykitBrowserPhoto } from '@/api/easykit';
import type { BrowserFolder, BrowserFile, RecentProject } from '@/api/easykit';
import type { PhotoItem } from '@/types/odoo';
import { PhotoLightbox } from '@/components/ui/PhotoLightbox';
import { PhotoGridThumb } from '@/components/ui/PhotoGridThumb';
import { isPdfPhoto } from '@/utils/photoFiles';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('nl-BE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function filesToPhotoItems(files: BrowserFile[]): PhotoItem[] {
  return files.map(f => ({
    key: f.key,
    url: f.url,
    filename: f.filename,
    lastModified: f.lastModified || null,
    size: f.size,
  }));
}

export function EasykitPhotoBrowserView() {
  const [prefix, setPrefix] = useState('easykit/');
  const [folders, setFolders] = useState<BrowserFolder[]>([]);
  const [files, setFiles] = useState<BrowserFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [recentProjects, setRecentProjects] = useState<RecentProject[]>([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Load recent projects on mount
  useEffect(() => {
    setRecentLoading(true);
    fetchEasykitRecentProjects(10)
      .then(data => setRecentProjects(data.projects))
      .catch(() => {})
      .finally(() => setRecentLoading(false));
  }, []);

  // Load folder contents when prefix changes
  const loadFolder = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchEasykitBrowse(prefix);
      setFolders(data.folders);
      setFiles(data.files);
    } catch {
      setFolders([]);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [prefix]);

  useEffect(() => {
    loadFolder();
  }, [loadFolder]);

  // Build breadcrumb from prefix
  // "easykit/Wille-Annaloro/levering/" -> ["easykit", "Wille-Annaloro", "levering"]
  const breadcrumbs = prefix.replace(/\/$/, '').split('/').filter(Boolean);

  function navigateTo(index: number) {
    // Build prefix up to and including the clicked breadcrumb segment
    const newPrefix = breadcrumbs.slice(0, index + 1).join('/') + '/';
    setPrefix(newPrefix);
  }

  function openFolder(folder: BrowserFolder) {
    setPrefix(folder.path);
  }

  // This browser lists whatever is in the bucket, including PDFs (foto-rapporten,
  // drukattesten). PhotoLightbox is an image viewer, so PDFs get their own tile.
  const imageFiles = files.filter((f) => !isPdfPhoto(f));

  return (
    <div className="px-4 lg:px-8 py-6">
      <h1 className="text-xl font-bold text-td-navy mb-6">EasyKit Foto's</h1>

      {/* Recent projects */}
      {prefix === 'easykit/' && (
        <div className="mb-8">
          <h2 className="text-sm font-bold text-td-navy mb-3">Recente projecten</h2>
          {recentLoading && (
            <div className="flex gap-3">
              {[1,2,3].map(i => <div key={i} className="w-32 h-20 bg-gray-100 rounded-lg animate-pulse" />)}
            </div>
          )}
          {!recentLoading && recentProjects.length === 0 && (
            <p className="text-sm text-td-muted">Nog geen projecten met foto's</p>
          )}
          {!recentLoading && recentProjects.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {recentProjects.map(project => (
                <button
                  key={project.path}
                  type="button"
                  onClick={() => setPrefix(project.path)}
                  className="text-left p-3 rounded-lg border border-gray-200 hover:border-td-blue hover:bg-td-blue/5 transition-colors min-w-[140px]"
                >
                  <p className="text-sm font-bold text-td-navy truncate">{project.name.replace(/-/g, ' ')}</p>
                  <p className="text-xs text-td-muted mt-1">{project.photoCount} foto's</p>
                  {project.lastModified && (
                    <p className="text-[10px] text-td-light mt-0.5">{formatDate(project.lastModified)}</p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-sm mb-4 flex-wrap">
        {breadcrumbs.map((segment, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <span className="text-gray-300">/</span>}
            {i < breadcrumbs.length - 1 ? (
              <button
                type="button"
                onClick={() => navigateTo(i)}
                className="text-td-blue hover:underline"
              >
                {segment.replace(/-/g, ' ')}
              </button>
            ) : (
              <span className="font-semibold text-td-navy">{segment.replace(/-/g, ' ')}</span>
            )}
          </span>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className="space-y-2">
          {[1,2,3].map(i => <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />)}
        </div>
      )}

      {/* Folders */}
      {!loading && folders.length > 0 && (
        <div className="space-y-1 mb-4">
          {folders.map(folder => (
            <button
              key={folder.path}
              type="button"
              onClick={() => openFolder(folder)}
              className="flex items-center gap-3 w-full text-left px-4 py-3 rounded-lg border border-gray-100 hover:bg-gray-50 hover:border-gray-200 transition-colors"
            >
              <svg className="w-5 h-5 text-amber-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
              </svg>
              <span className="text-sm font-medium text-td-navy">{folder.name.replace(/-/g, ' ')}</span>
              <svg className="w-4 h-4 text-gray-300 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ))}
        </div>
      )}

      {/* Files (photo grid) */}
      {!loading && files.length > 0 && (
        <div>
          <p className="text-xs text-td-muted mb-3">{files.length} foto's</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {files.map((file, i) => (
              <div key={file.key} className="group relative aspect-square rounded-lg overflow-hidden border border-gray-200 hover:border-td-blue transition-colors">
                <PhotoGridThumb
                  photo={file}
                  tileSizeClass="w-full h-full"
                  imgClassName="w-full h-full object-cover"
                  onOpen={() => setLightboxIndex(imageFiles.findIndex((f) => f.key === file.key))}
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-[10px] text-white truncate">{file.filename}</p>
                  <p className="text-[9px] text-white/70">{formatSize(file.size)}</p>
                </div>
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await deleteEasykitBrowserPhoto(file.key);
                      setFiles(prev => prev.filter(f => f.key !== file.key));
                    } catch { /* silent */ }
                  }}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Verwijderen"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && folders.length === 0 && files.length === 0 && (
        <div className="text-center py-16">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <p className="text-td-muted text-sm">Geen bestanden in deze map</p>
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={filesToPhotoItems(imageFiles)}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
